import { buttonClassName } from '@emailforge/ui';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Icon } from '@/components/ui/icons';
import { useGetPricingQuery } from '@/features/billing/billingApi';

function formatEurMinor(minor: number): string {
  return `€${Math.round(minor / 100)}`;
}

/**
 * Marketing landing — v2 design adapted to our routing + EmailOps positioning.
 * The visual structure follows the v2 prototype (hero with code card, dedicated
 * deliverability section with health metrics + DNS state + reputation,
 * 6-card feature grid, dark dev-section with curl example, pricing preview,
 * testimonials, SMTP provider showcase, final CTA). Copy keeps the
 * "EmailOps for developers" framing — we did not adopt the v2 "Git for
 * emails" tagline because that conflicts with the repositioning shipped on
 * 2026-05-01 (PROGRESS.md "Poda EmailOps").
 */
export function MainLanding() {
  return (
    <>
      {/* HERO */}
      <section style={{ position: 'relative', overflow: 'hidden', padding: '80px 40px 64px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.15fr 1fr',
            gap: 48,
            alignItems: 'center',
            maxWidth: 1280,
            margin: '0 auto',
          }}
        >
          <div>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '4px 10px',
                borderRadius: 999,
                border: '1px solid var(--flame-line)',
                background: 'var(--flame-soft)',
                color: 'var(--flame-2)',
              }}
            >
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--flame)' }} />
              <span
                className="mono"
                style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.08 }}
              >
                EmailOps · for developers
              </span>
            </div>
            <h1
              className="display"
              style={{
                fontSize: 84,
                lineHeight: 1.02,
                letterSpacing: -0.028,
                marginTop: 20,
                margin: '20px 0 0',
              }}
            >
              Forge emails. <i style={{ color: 'var(--flame)' }}>Operate</i> them. Ship them.
            </h1>
            <p
              style={{
                fontSize: 18,
                color: 'var(--ink-2)',
                marginTop: 22,
                maxWidth: 540,
                lineHeight: 1.5,
              }}
            >
              A React Email editor with{' '}
              <span
                className="mono"
                style={{
                  fontSize: 14,
                  padding: '1px 6px',
                  background: 'var(--paper-2)',
                  border: '1px solid var(--line-soft)',
                  borderRadius: 4,
                }}
              >
                Delivery Health
              </span>{' '}
              built in. Version templates, roll back deploys, and ship through your own SMTP — all from one canvas.
            </p>
            <div style={{ display: 'flex', gap: 10, marginTop: 30 }}>
              <Link to="/signup" className={buttonClassName({ variant: 'flame' })} style={{ height: 40, padding: '0 18px', fontSize: 14 }}>
                Start free <Icon.arrow size={13} />
              </Link>
              <Link to="/pricing" className={buttonClassName()} style={{ height: 40, padding: '0 18px', fontSize: 14 }}>
                See pricing
              </Link>
            </div>
            <div
              className="mono"
              style={{
                fontSize: 11,
                color: 'var(--ink-3)',
                marginTop: 20,
                textTransform: 'uppercase',
                letterSpacing: 0.06,
              }}
            >
              BYO-SMTP · Templates as code · Versions · Rollback · A/B
            </div>
          </div>

          <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
            <EmailMock />
            {/* Floating code card */}
            <div
              style={{
                position: 'absolute',
                bottom: -30,
                left: -20,
                width: 260,
                background: 'var(--ink)',
                borderRadius: 7,
                padding: '12px 14px',
                color: 'white',
                boxShadow: '0 20px 40px -12px oklch(0.18 0.01 85 / 0.3)',
              }}
            >
              <div className="mono" style={{ fontSize: 10, color: 'oklch(0.72 0.01 85)' }}>
                POST /api/send/welcome-day-0
              </div>
              <div className="mono" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.5 }}>
                <span style={{ color: 'oklch(0.72 0.14 148)' }}>"first_name"</span>:{' '}
                <span style={{ color: 'oklch(0.80 0.14 85)' }}>"Marga"</span>,
                <br />
                <span style={{ color: 'oklch(0.72 0.14 148)' }}>"plan"</span>:{' '}
                <span style={{ color: 'oklch(0.80 0.14 85)' }}>"pro"</span>
              </div>
              <div
                className="mono"
                style={{
                  fontSize: 10,
                  color: 'oklch(0.78 0.14 148)',
                  marginTop: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'oklch(0.72 0.14 148)' }} />
                200 OK · 142ms
              </div>
            </div>
          </div>
        </div>
        {/* Bg dots */}
        <div style={{ position: 'absolute', top: 40, right: 40, opacity: 0.25, pointerEvents: 'none' }}>
          <svg width="180" height="180" aria-hidden="true">
            <pattern id="dot" x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1" fill="var(--ink-3)" />
            </pattern>
            <rect width="180" height="180" fill="url(#dot)" />
          </svg>
        </div>
      </section>

      {/* DELIVERABILITY */}
      <DeliverabilitySection />

      {/* FEATURES GRID */}
      <FeaturesSection />

      {/* DEV SECTION */}
      <DevSection />

      {/* PRICING preview */}
      <PricingPreview />

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* SMTP PROVIDERS */}
      <ProvidersShowcase />

      {/* FINAL CTA */}
      <section
        style={{
          padding: '96px 40px',
          textAlign: 'center',
          background: 'var(--paper)',
          borderTop: '1px solid var(--line)',
        }}
      >
        <div
          className="display"
          style={{ fontSize: 72, letterSpacing: -0.028, lineHeight: 1.05, maxWidth: 1000, margin: '0 auto' }}
        >
          Your templates. Your SMTP. <i style={{ color: 'var(--flame)' }}>Your call.</i>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 32 }}>
          <Link to="/signup" className={buttonClassName({ variant: 'flame' })} style={{ height: 44, padding: '0 22px', fontSize: 14 }}>
            Start free — no card required
          </Link>
        </div>
        <div
          className="mono"
          style={{
            fontSize: 11,
            color: 'var(--ink-3)',
            marginTop: 18,
            textTransform: 'uppercase',
            letterSpacing: 0.08,
          }}
        >
          Free forever · 3 templates · Export unlimited
        </div>
      </section>
    </>
  );
}

// ----------------------------------------------------------------------------
// Sections

function DeliverabilitySection() {
  return (
    <section
      style={{
        padding: '72px 40px',
        borderTop: '1px solid var(--line)',
        background: 'var(--paper-2)',
      }}
    >
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div
          className="mono"
          style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: 0.08 }}
        >
          02 · Deliverability that's actually visible
        </div>
        <h2
          className="display"
          style={{ fontSize: 44, letterSpacing: -0.02, marginTop: 8, maxWidth: 780, lineHeight: 1.1 }}
        >
          Most editors stop at <i style={{ color: 'var(--flame)' }}>"looks good in Gmail"</i>.
        </h2>
        <p style={{ fontSize: 15, color: 'var(--ink-2)', marginTop: 12, maxWidth: 620 }}>
          Emailforge surfaces the operational layer: provider success rates, p50/p95 latency, hard
          bounce taxonomy, and SPF/DKIM/DMARC alignment in real time.
        </p>

        <div style={{ marginTop: 36, display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
          {/* Health card */}
          <div
            className="mf-card"
            style={{ background: 'var(--paper)', padding: '24px 26px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                className="mono"
                style={{
                  fontSize: 11,
                  color: 'var(--ink-3)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.08,
                  flex: 1,
                }}
              >
                Last 24h · acme.inc
              </div>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'oklch(0.65 0.16 148)' }} />
              <span
                className="mono"
                style={{ fontSize: 11, color: 'oklch(0.45 0.14 148)', fontWeight: 600 }}
              >
                HEALTHY
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 24, marginTop: 18 }}>
              {[
                { l: 'Delivered', v: '98.7%', s: '+0.3%', g: true },
                { l: 'p50 latency', v: '189ms', s: '·', mono: true },
                { l: 'p95 latency', v: '612ms', s: '·', mono: true },
                { l: 'Hard bounce', v: '0.4%', s: '−0.1%', g: true },
              ].map((m) => (
                <div key={m.l}>
                  <div
                    className="mono"
                    style={{
                      fontSize: 10.5,
                      color: 'var(--ink-3)',
                      textTransform: 'uppercase',
                      letterSpacing: 0.08,
                    }}
                  >
                    {m.l}
                  </div>
                  <div
                    className={m.mono ? 'mono' : 'display'}
                    style={{
                      fontSize: m.mono ? 22 : 30,
                      letterSpacing: m.mono ? 0 : -0.015,
                      marginTop: 6,
                      fontWeight: 600,
                    }}
                  >
                    {m.v}
                  </div>
                  <div
                    className="mono"
                    style={{
                      fontSize: 10.5,
                      color: m.g ? 'oklch(0.5 0.14 148)' : 'var(--ink-3)',
                      marginTop: 2,
                    }}
                  >
                    {m.s}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ height: 1, background: 'var(--line-soft)', margin: '20px 0 14px' }} />
            <div
              className="mono"
              style={{
                fontSize: 10.5,
                color: 'var(--ink-3)',
                textTransform: 'uppercase',
                letterSpacing: 0.08,
                marginBottom: 10,
              }}
            >
              Hard bounce taxonomy · last 24h
            </div>
            {[
              { d: 'Invalid mailbox', n: '14', r: 62 },
              { d: 'Domain not found', n: '5', r: 22 },
              { d: 'Mailbox full', n: '2', r: 9 },
              { d: 'Spam blocked', n: '2', r: 7 },
            ].map((d, i) => (
              <div
                key={d.d}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '160px 1fr 60px 50px',
                  alignItems: 'center',
                  gap: 12,
                  padding: '8px 0',
                  borderBottom: i < 3 ? '1px solid var(--line-soft)' : 'none',
                }}
              >
                <span className="mono" style={{ fontSize: 12 }}>
                  {d.d}
                </span>
                <div style={{ height: 6, background: 'var(--paper-2)', borderRadius: 3, overflow: 'hidden' }}>
                  <div
                    style={{
                      width: `${d.r}%`,
                      height: '100%',
                      background: i === 0 ? 'oklch(0.65 0.18 30)' : 'var(--flame)',
                    }}
                  />
                </div>
                <span
                  className="mono"
                  style={{ fontSize: 11.5, color: 'var(--ink-2)', textAlign: 'right' }}
                >
                  {d.n} hits
                </span>
                <span
                  className="mono"
                  style={{ fontSize: 11.5, fontWeight: 600, textAlign: 'right' }}
                >
                  {d.r}%
                </span>
              </div>
            ))}
          </div>

          {/* Side stack: DNS card + reputation */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="mf-card" style={{ background: 'var(--paper)', padding: '18px 20px' }}>
              <div
                className="mono"
                style={{
                  fontSize: 10.5,
                  color: 'var(--ink-3)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.08,
                }}
              >
                DNS · acme.inc
              </div>
              {[
                ['SPF', 'v=spf1 include:emailforge.dev'],
                ['DKIM', 's=ef1 · 2048-bit'],
                ['DMARC', 'p=quarantine · pct=100'],
                ['MX', 'mail.acme.inc · prio 10'],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: 'oklch(0.65 0.16 148)',
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="mono" style={{ fontSize: 11.5, fontWeight: 600 }}>
                      {k}
                    </div>
                    <div
                      className="mono"
                      style={{
                        fontSize: 10,
                        color: 'var(--ink-3)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {v}
                    </div>
                  </div>
                  <span
                    className="mono"
                    style={{ fontSize: 10, color: 'oklch(0.45 0.14 148)', fontWeight: 600 }}
                  >
                    OK
                  </span>
                </div>
              ))}
            </div>
            <div
              style={{
                background: 'var(--ink)',
                color: 'white',
                borderRadius: 'var(--r-3)',
                padding: '18px 20px',
              }}
            >
              <div
                className="mono"
                style={{
                  fontSize: 10.5,
                  color: 'oklch(0.65 0.01 85)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.08,
                }}
              >
                Deliverability score
              </div>
              <div
                className="display"
                style={{ fontSize: 36, marginTop: 6, letterSpacing: -0.015 }}
              >
                94<span style={{ fontSize: 18, color: 'oklch(0.65 0.01 85)' }}>/100</span>
              </div>
              <div
                className="mono"
                style={{ fontSize: 10.5, color: 'oklch(0.65 0.01 85)', marginTop: 4 }}
              >
                delivered − bounced − complained
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  const features = [
    {
      n: '01',
      t: 'Editor',
      h: 'Drag, drop, export.',
      d: 'Visual canvas over React Email. What you see is what ships — and you can always open the .tsx.',
    },
    {
      n: '02',
      t: 'Preview',
      h: '9 clients. One click.',
      d: 'Gmail, Outlook, Apple Mail, iOS, Android, dark mode. Server-rendered screenshots before send.',
    },
    {
      n: '03',
      t: 'Sending',
      h: 'Your SMTP, not ours.',
      d: 'Connect Resend, Postmark, SendGrid, SES, Mailgun, or plain SMTP. We render, you send.',
    },
    {
      n: '04',
      t: 'Versioning',
      h: 'Versions & rollback.',
      d: 'Every save is a version. Diff between any two. Roll back in one click when production cries.',
    },
    {
      n: '05',
      t: 'A/B',
      h: 'Pick a winner automatically.',
      d: 'Declare metric, traffic split, minimum sample. We declare the winner and switch the template.',
    },
    {
      n: '06',
      t: 'Surface area',
      h: 'API. Webhooks. CLI.',
      d: 'POST to /send. Receive delivery events. Deploy templates from CI. It\'s all automate-first.',
    },
  ];
  return (
    <section style={{ padding: '80px 40px' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div
          className="mono"
          style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: 0.08 }}
        >
          03 · What's in the box
        </div>
        <h2 className="display" style={{ fontSize: 44, letterSpacing: -0.02, marginTop: 8 }}>
          Everything you'd build yourself. <i style={{ color: 'var(--flame)' }}>Without building it.</i>
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginTop: 44 }}>
          {features.map((f) => (
            <div
              key={f.n}
              className="mf-card"
              style={{ padding: '24px 24px 28px', background: 'var(--paper)' }}
            >
              <div className="mono" style={{ fontSize: 11, color: 'var(--flame)', letterSpacing: 0.1 }}>
                {f.n} · {f.t}
              </div>
              <div
                className="display"
                style={{ fontSize: 26, letterSpacing: -0.015, marginTop: 14, lineHeight: 1.1 }}
              >
                {f.h}
              </div>
              <div style={{ fontSize: 13.5, color: 'var(--ink-2)', marginTop: 10, lineHeight: 1.55 }}>
                {f.d}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DevSection() {
  return (
    <section style={{ padding: '72px 40px', background: 'var(--ink)', color: 'white' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div
          className="mono"
          style={{
            fontSize: 11,
            color: 'oklch(0.65 0.01 85)',
            textTransform: 'uppercase',
            letterSpacing: 0.08,
          }}
        >
          04 · Built for devs who hate writing HTML
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 48,
            marginTop: 20,
            alignItems: 'center',
          }}
        >
          <div>
            <h2
              className="display"
              style={{ fontSize: 44, letterSpacing: -0.02, lineHeight: 1.08, color: 'white', margin: 0 }}
            >
              Fire it from <i style={{ color: 'var(--flame)' }}>your backend</i>.
            </h2>
            <p
              style={{
                fontSize: 15,
                color: 'oklch(0.75 0.01 85)',
                marginTop: 16,
                maxWidth: 460,
                lineHeight: 1.55,
              }}
            >
              Every template you publish becomes an HTTPS endpoint. Your app — or any tool that can make an
              HTTP request (Zapier, n8n, Make, your CRM webhook) — fires it with a payload. We handle render,
              send, retries, and tracking.
            </p>
            <pre
              style={{
                marginTop: 28,
                fontFamily: 'var(--font-mono)',
                fontSize: 12.5,
                background: 'oklch(0.12 0.005 85)',
                border: '1px solid oklch(0.28 0.005 85)',
                borderRadius: 8,
                padding: '18px 20px',
                lineHeight: 1.7,
                whiteSpace: 'pre-wrap',
              }}
            >
              <code>
                <span style={{ color: 'oklch(0.62 0.01 85)' }}>{'// Send the welcome email'}</span>
                {'\n'}
                <span style={{ color: 'oklch(0.80 0.12 240)' }}>curl</span> -X POST
                https://api.emailforge.app/api/send/<span style={{ color: 'oklch(0.80 0.14 85)' }}>welcome-day-0</span>{' '}
                {'\\\n'}
                {'  '}-H{' '}
                <span style={{ color: 'oklch(0.78 0.14 148)' }}>"Authorization: Bearer $KEY"</span> {'\\\n'}
                {'  '}-d{' '}
                <span style={{ color: 'oklch(0.78 0.14 148)' }}>{'\'{ "to": "marga@acme.inc", "first_name": "Marga" }\''}</span>
                {'\n'}
                <span style={{ color: 'oklch(0.62 0.01 85)' }}>{'// → 200 OK · sendEventId=evt_8Hn4'}</span>
              </code>
            </pre>
            <div
              style={{
                marginTop: 16,
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 10,
              }}
            >
              <span
                className="mono"
                style={{
                  fontSize: 10.5,
                  color: 'oklch(0.65 0.01 85)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.08,
                }}
              >
                Works with
              </span>
              {['your backend', 'Zapier', 'n8n', 'Make', 'any HTTP webhook'].map((tool, i) => (
                <span
                  key={tool}
                  className="mono"
                  style={{
                    fontSize: 11,
                    color: 'oklch(0.78 0.01 85)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  {tool}
                  {i < 4 && <span style={{ color: 'oklch(0.45 0.01 85)' }}>·</span>}
                </span>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <EmailMock />
          </div>
        </div>
      </div>
    </section>
  );
}

function PricingPreview() {
  const [billing, setBilling] = useState<'monthly' | 'annual'>('annual');
  const annual = billing === 'annual';
  const { data: pricing } = useGetPricingQuery();

  // Helper: pick a {monthly, annual} pair with fallback to hardcoded EUR amounts.
  const sub = (apiKey: 'starter' | 'pro', fallback: { monthly: number; annual: number }) => {
    const live = pricing?.[apiKey];
    const monthly = live?.monthly?.amount ?? fallback.monthly * 100;
    const yearly = live?.yearly?.amount ?? fallback.annual * 100;
    return {
      p: annual ? formatEurMinor(yearly) : formatEurMinor(monthly),
      old: annual ? formatEurMinor(monthly * 12) : undefined,
      u: annual ? 'per year' : 'per month',
    };
  };
  const exportPrice = pricing?.exportPack.once?.amount;
  const exportP = exportPrice != null ? formatEurMinor(exportPrice) : '€29';

  type LandingPlan = {
    n: string;
    eye: string;
    p: string;
    old?: string;
    u: string;
    fs: string[];
    cta: string;
    to: string;
    flame?: boolean;
    chip?: string;
  };
  const plans: LandingPlan[] = [
    {
      n: 'Free',
      eye: 'For exploring',
      p: '€0',
      u: 'forever',
      fs: ['3 templates', 'Gmail + Apple preview', 'Export to .tsx & HTML', 'Community support'],
      cta: 'Start free',
      to: '/signup',
    },
    {
      n: 'Export Pack',
      eye: 'For one-off projects',
      p: exportP,
      u: 'once · 90 days',
      fs: ['10 templates total', 'Unlimited export', 'Full library to fork', 'No watermark', 'No sending'],
      cta: 'Get Export Pack',
      to: '/checkout/export-pack',
    },
    {
      n: 'Starter',
      eye: 'For shipping apps',
      ...sub('starter', { monthly: 29, annual: 290 }),
      fs: ['10 published templates', '1 sender verified', '1,000 emails/mo', '3 triggers', 'Versioning (last 10)'],
      cta: 'Start Starter',
      flame: true,
      chip: 'Most popular',
      to: '/checkout/starter',
    },
    {
      n: 'Pro',
      eye: 'For growing teams',
      ...sub('pro', { monthly: 79, annual: 790 }),
      fs: [
        'Unlimited templates',
        '5 senders',
        '10,000 emails/mo',
        'Unlimited triggers',
        'A/B · versions',
        'Custom tracking domain',
      ],
      cta: 'Start Pro',
      to: '/checkout/pro',
    },
  ];
  return (
    <section
      style={{ padding: '80px 40px', background: 'var(--paper-2)', borderTop: '1px solid var(--line)' }}
    >
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 24,
            marginBottom: 30,
          }}
        >
          <div>
            <div
              className="mono"
              style={{
                fontSize: 11,
                color: 'var(--ink-3)',
                textTransform: 'uppercase',
                letterSpacing: 0.08,
              }}
            >
              05 · Pricing
            </div>
            <h2 className="display" style={{ fontSize: 44, letterSpacing: -0.02, marginTop: 8 }}>
              Free forever, <i style={{ color: 'var(--flame)' }}>or all of it</i>.
            </h2>
            <p style={{ fontSize: 14, color: 'var(--ink-2)', marginTop: 8, maxWidth: 520 }}>
              Annual billing — save 2 months. Switch to monthly any time.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 6 }}>
            <div
              role="tablist"
              aria-label="Billing period"
              style={{
                display: 'inline-flex',
                border: '1px solid var(--line)',
                borderRadius: 999,
                padding: 3,
                background: 'var(--paper)',
              }}
            >
              {(['monthly', 'annual'] as const).map((b) => (
                <button
                  key={b}
                  type="button"
                  role="tab"
                  aria-selected={billing === b}
                  onClick={() => setBilling(b)}
                  style={{
                    padding: '4px 14px',
                    fontSize: 12.5,
                    background: billing === b ? 'var(--ink)' : 'transparent',
                    color: billing === b ? 'white' : 'var(--ink-3)',
                    borderRadius: 999,
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontWeight: billing === b ? 550 : 400,
                  }}
                >
                  {b === 'monthly' ? 'Monthly' : 'Annual'}
                </button>
              ))}
            </div>
            <span className="mono" style={{ fontSize: 11, color: 'var(--flame)', fontWeight: 600 }}>
              −17%
            </span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
          {plans.map((p) => (
            <div
              key={p.n}
              style={{
                position: 'relative',
                background: 'var(--paper)',
                border: p.flame ? '1.5px solid var(--flame)' : '1px solid var(--line)',
                borderRadius: 'var(--r-3)',
                padding: '22px 20px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {p.chip && (
                <div
                  style={{
                    position: 'absolute',
                    top: -10,
                    right: 16,
                    background: 'var(--flame)',
                    color: 'white',
                    padding: '2px 8px',
                    fontSize: 10.5,
                    borderRadius: 999,
                    fontWeight: 550,
                  }}
                >
                  {p.chip}
                </div>
              )}
              <div
                className="mono"
                style={{
                  fontSize: 10.5,
                  color: 'var(--ink-3)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.08,
                }}
              >
                {p.eye}
              </div>
              <div style={{ fontSize: 20, fontWeight: 600, marginTop: 4 }}>{p.n}</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 14 }}>
                <div className="display" style={{ fontSize: 42, letterSpacing: -0.02, lineHeight: 1 }}>
                  {p.p}
                </div>
                {p.old && (
                  <div
                    className="mono"
                    style={{ fontSize: 11, color: 'var(--ink-3)', textDecoration: 'line-through' }}
                  >
                    {p.old}
                  </div>
                )}
              </div>
              <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                {p.u}
              </div>
              <div style={{ height: 1, background: 'var(--line-soft)', margin: '18px 0' }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                {p.fs.map((f) => (
                  <div key={f} style={{ display: 'flex', gap: 8, fontSize: 13, color: 'var(--ink-2)' }}>
                    <span style={{ color: 'oklch(0.55 0.14 148)', marginTop: 1 }}>
                      <Icon.check size={13} />
                    </span>
                    <span>{f}</span>
                  </div>
                ))}
              </div>
              <Link
                to={p.to}
                className={buttonClassName({ variant: p.flame ? 'flame' : 'default' })}
                style={{ height: 34, marginTop: 20, justifyContent: 'center', width: '100%' }}
              >
                {p.cta}
              </Link>
            </div>
          ))}
        </div>

        <div
          className="mf-card"
          style={{
            marginTop: 20,
            padding: '14px 18px',
            background: 'var(--paper)',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}
        >
          <div style={{ flex: 1 }}>
            <span style={{ fontWeight: 550 }}>Need volume, SSO, or EU data residency?</span>
            <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', marginLeft: 14 }}>
              SAML · SCIM · DPA · 99.99% SLA
            </span>
          </div>
          <Link
            to="/pricing"
            style={{
              color: 'var(--ink-2)',
              fontSize: 13,
              fontWeight: 550,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              paddingRight: 14,
              borderRight: '1px solid var(--line)',
              textDecoration: 'none',
            }}
          >
            See full comparison →
          </Link>
          <Link
            to="/contact"
            style={{
              color: 'var(--flame)',
              fontSize: 13,
              fontWeight: 550,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              textDecoration: 'none',
            }}
          >
            Talk to sales <Icon.arrow size={13} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const items = [
    {
      q: "Swapped Mailchimp for Emailforge in an afternoon. The export-to-tsx alone would've been worth it — having the editor on top is absurd.",
      n: 'Tomás R.',
      t: 'CTO · Relay · Berlin',
      init: 'TR',
      color: 'oklch(0.70 0.13 240)',
    },
    {
      q: 'I ship transactional emails three times a week. The A/B flow picks the winner while I sleep. I got three hours a week back.',
      n: 'Nadia K.',
      t: 'Growth lead · Frequency · Lisbon',
      init: 'NK',
      color: 'oklch(0.68 0.14 148)',
    },
    {
      q: "The preview in Outlook 365 is the first tool in 10 years that renders exactly what I'll see. I don't know how else to say it.",
      n: 'Marga S.',
      t: 'Founder · Kilobyte · Madrid',
      init: 'MS',
      color: 'oklch(0.72 0.09 45)',
    },
  ];
  return (
    <section style={{ padding: '72px 40px' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div
          className="mono"
          style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: 0.08 }}
        >
          06 · Teams building with Emailforge
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginTop: 24 }}>
          {items.map((t) => (
            <div
              key={t.n}
              className="mf-card"
              style={{ padding: '22px 22px', background: 'var(--paper)' }}
            >
              <div style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink)' }}>"{t.q}"</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 20 }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: t.color,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  {t.init}
                </div>
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 550 }}>{t.n}</div>
                  <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>
                    {t.t}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProvidersShowcase() {
  const providers = [
    { n: 'Resend', d: 'API · 1.3M sent', tag: 'Recommended' },
    { n: 'Postmark', d: 'API · 480k sent' },
    { n: 'AWS SES', d: 'SMTP · 2.1M sent' },
    { n: 'SendGrid', d: 'API · 312k sent' },
    { n: 'Mailgun', d: 'API · 188k sent' },
    { n: 'Postfix', d: 'SMTP · 24k sent' },
    { n: 'Sparkpost', d: 'API · 51k sent' },
    { n: 'Custom SMTP', d: 'host:port · TLS' },
  ];
  return (
    <section
      style={{ padding: '56px 40px', borderTop: '1px solid var(--line)', background: 'var(--paper-2)' }}
    >
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <div>
            <div
              className="mono"
              style={{
                fontSize: 11,
                color: 'var(--ink-3)',
                textTransform: 'uppercase',
                letterSpacing: 0.08,
              }}
            >
              07 · BYO infra
            </div>
            <h2 className="display" style={{ fontSize: 32, letterSpacing: -0.015, marginTop: 6 }}>
              Bring any SMTP. <i style={{ color: 'var(--flame)' }}>Or none</i>.
            </h2>
            <p style={{ fontSize: 14, color: 'var(--ink-2)', marginTop: 8, maxWidth: 520 }}>
              We render and ship. Your provider delivers. Switch providers without changing a single template.
            </p>
          </div>
          <Link
            to="/docs"
            className="mono"
            style={{ fontSize: 12, color: 'var(--flame)', textDecoration: 'none' }}
          >
            Provider docs →
          </Link>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 12,
            marginTop: 28,
          }}
        >
          {providers.map((p) => (
            <div
              key={p.n}
              className="mf-card"
              style={{ position: 'relative', padding: '14px 16px', background: 'var(--paper)' }}
            >
              {p.tag && (
                <div
                  className="mono"
                  style={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    fontSize: 9.5,
                    color: 'var(--flame)',
                    textTransform: 'uppercase',
                    letterSpacing: 0.08,
                    fontWeight: 600,
                  }}
                >
                  {p.tag}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 5,
                    background: 'var(--paper-2)',
                    border: '1px solid var(--line-soft)',
                  }}
                />
                <span style={{ fontSize: 13.5, fontWeight: 600 }}>{p.n}</span>
              </div>
              <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)', marginTop: 8 }}>
                {p.d}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Visual mock of a rendered email — used in the hero + dev section so users
 * see what the editor produces. Not interactive by design; clicks fall
 * through to the section CTAs.
 */
function EmailMock() {
  return (
    <div
      style={{
        width: 440,
        background: 'white',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-3)',
        boxShadow: 'var(--sh-md)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          height: 34,
          background: 'var(--paper-2)',
          borderBottom: '1px solid var(--line)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 10px',
          gap: 10,
        }}
      >
        <div className="mf-tl-dots">
          <i />
          <i />
          <i />
        </div>
        <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', flex: 1, textAlign: 'center' }}>
          welcome to acme — let's get you set up
        </span>
      </div>
      <div style={{ padding: 28 }}>
        <div
          style={{
            height: 120,
            borderRadius: 'var(--r-2)',
            background:
              'linear-gradient(135deg, oklch(0.72 0.14 32) 0%, oklch(0.66 0.16 48) 100%)',
            marginBottom: 18,
          }}
        />
        <div
          className="display"
          style={{ fontSize: 24, fontWeight: 500, letterSpacing: -0.01, color: '#2a251f' }}
        >
          Welcome, Marga.
        </div>
        <div style={{ fontSize: 13, color: '#5a4a38', marginTop: 8, lineHeight: 1.5 }}>
          You're all set. Here's a 5-minute tour of what your team can do next.
        </div>
        <div
          style={{
            display: 'inline-block',
            marginTop: 18,
            padding: '8px 16px',
            background: 'var(--flame)',
            color: 'white',
            borderRadius: 'var(--r-2)',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          Track my order
        </div>
        <div
          style={{
            marginTop: 24,
            paddingTop: 16,
            borderTop: '1px solid #ece8e0',
            fontSize: 11,
            color: '#a89c8a',
          }}
        >
          acme.inc · Berlin · unsubscribe
        </div>
      </div>
    </div>
  );
}
